import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import type { PropertyType } from "@/lib/supabase/types";
import { canAccess } from "@/lib/rbac";

interface ProtectedRouteProps {
  children: ReactNode;
  /**
   * If set, the caller's org must have a property of this type (a
   * module) to access the route. Example: the Rooms page passes
   * `requireModule="hotel"` so a rental-only tenant is redirected
   * to /dashboard instead of seeing a hotel-only surface.
   *
   * When missing (undefined), no module check is performed — route
   * is protected by session only.
   */
  requireModule?: PropertyType;
}

export default function ProtectedRoute({ children, requireModule }: ProtectedRouteProps) {
  const { session, loading, hasHotel, hasRental, modulesReady, currentRole } = useAuth();
  const location = useLocation();

  const loader = (
    <div className="min-h-screen bg-cream flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-ink flex items-center justify-center">
          <i className="ri-door-open-line text-white text-xl"></i>
        </div>
        <div className="flex items-center gap-2 text-muted text-sm">
          <i className="ri-loader-4-line animate-spin text-gold"></i>
          <span className="font-tajawal">
            {localStorage.getItem("diyafa_lang") === "ar" ? "جارٍ التحميل..." : "Loading..."}
          </span>
        </div>
      </div>
    </div>
  );

  if (loading) return loader;

  if (!session) {
    return <Navigate to="/" replace />;
  }

  // Module guard. Wait until memberships + properties have loaded —
  // before that hasHotel/hasRental are still false, and redirecting
  // would bounce every refresh or deep link to /dashboard.
  if (requireModule && !modulesReady) return loader;
  if (requireModule) {
    const allowed = requireModule === "hotel" ? hasHotel : hasRental;
    if (!allowed) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  // Role gate. If we know the role and RBAC denies the path, bounce to
  // the dashboard home. (Housekeeping hitting /billing, for example.)
  if (currentRole && !canAccess(currentRole, location.pathname)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
